/**
 * JobClose AI — shared customer-facing disclosure helper.
 *
 * Single source of truth for how `allowance` and `tm` (Time & Materials)
 * line items are surfaced to customers. Phase 2 Step 4 (Jun 2026) extracts
 * this from the four React rendering surfaces (PublicDocs, Proposals,
 * Contracts, CustomerPreview) so that any future change to disclosure
 * wording, classification rules, or fallback derivation only needs to
 * happen in one place.
 *
 * Rules we enforce here (must match `/app/backend/disclosure.py`):
 *   1. T&M line items NEVER appear inline with firm-priced items. They are
 *      partitioned into a separate "Possible Additional Work (T&M)" list.
 *   2. T&M dollar amounts are EXCLUDED from the binding proposal total.
 *      The reserve is surfaced as a separate "Possible T&M reserve" line.
 *   3. Allowance line items stay in the firm pricing table (their amounts
 *      ARE included in the total — they are placeholder firm amounts), but
 *      they're tagged with an "Allowance" badge + footnote so the customer
 *      knows the final cost can shift via change order.
 *   4. The internal `pricing_type` enum value is NEVER exposed to the
 *      customer. Only the customer-friendly "Allowance" label is shown.
 *   5. When the persisted `snap.totals` predates Phase-2-Step-1 and lacks
 *      `allowance_total` / `tm_estimate`, derive them from `snap.line_items`
 *      on the fly. Renderer-only — never mutates persisted data.
 */

// Bilingual customer-facing wording. The keys here are the only labels the
// customer ever sees; the raw `pricing_type` enum stays internal forever.
export const DISCLOSURE = {
  en: {
    allowance_badge: "Allowance",
    allowance_legend:
      "Allowance items are placeholder amounts. Final cost may vary based on selections or conditions and will be handled through a written change order.",
    tm_section_title: "Possible Additional Work (Time & Materials)",
    tm_legend:
      "These items are estimates only and may be billed if the work becomes necessary.",
    tm_reserve_label: "Possible T&M reserve",
    tm_reserve_note: "not included in firm total",
    allowance_total_label: "Includes allowances",
    item: "Item",
    qty: "Qty",
    est_total: "Est. total",
  },
  es: {
    allowance_badge: "Provisión",
    allowance_legend:
      "Los rubros de provisión son montos preliminares. El costo final puede variar según las selecciones o condiciones y se gestionará mediante una orden de cambio por escrito.",
    tm_section_title: "Trabajo adicional posible (tiempo y materiales)",
    tm_legend:
      "Estos rubros son estimados y pueden facturarse si el trabajo resulta necesario.",
    tm_reserve_label: "Reserva posible T&M",
    tm_reserve_note: "no incluida en el total firme",
    allowance_total_label: "Incluye provisiones",
    item: "Artículo",
    qty: "Cant.",
    est_total: "Total est.",
  },
};

/** Resolve a disclosure label bundle, falling back to English. */
export const labels = (lang) => DISCLOSURE[lang] || DISCLOSURE.en;

/** True when a line item is contractor-tagged as an allowance. */
export const isAllowance = (li) =>
  (li?.pricing_type || "").toLowerCase() === "allowance";

/** True when a line item is contractor-tagged as Time & Materials. */
export const isTm = (li) =>
  (li?.pricing_type || "").toLowerCase() === "tm";

/**
 * Public-payload variant: when the safe payload has stripped `pricing_type`
 * but kept the `is_allowance` / `is_tm` booleans, use those instead.
 * Falls back to the raw enum check for authenticated callers.
 */
export const isAllowanceLine = (li) =>
  li?.is_allowance === true || isAllowance(li);
export const isTmLine = (li) => li?.is_tm === true || isTm(li);

/**
 * Partition line items into firm-priced (rendered in the main pricing
 * table) and T&M (rendered in the separate "Possible Additional Work"
 * section). Pure function — never mutates the input.
 *
 * Accepts BOTH the public-payload shape (no `pricing_type`, has
 * `is_allowance`/`is_tm`) and the authenticated shape (has `pricing_type`).
 */
export const partitionLineItems = (lineItems) => {
  const firm = [];
  const tm = [];
  for (const li of lineItems || []) {
    if (isTmLine(li)) tm.push(li);
    else firm.push(li);
  }
  return { firm, tm };
};

const _sumTotals = (items, predicate) =>
  (items || [])
    .filter(predicate)
    .reduce((acc, l) => acc + Number(l.total || 0), 0);

/**
 * Derive the customer-facing disclosure aggregates from a snapshot.
 *
 * Prefers the persisted `snap.totals.allowance_total` /
 * `snap.totals.tm_estimate` when present (these are populated by the
 * Phase-2-Step-1 backend math), and falls back to summing
 * `snap.line_items[]` on the fly when the snapshot predates that change.
 *
 * Returns numeric values (never NaN / undefined) so the caller can do a
 * simple `> 0` gate to decide whether to render the disclosure rows.
 */
export const deriveDisclosureTotals = (snap) => {
  const totals = snap?.totals || {};
  const items = snap?.line_items || [];
  const persistedAllowance = Number(totals.allowance_total || 0);
  const persistedTm = Number(totals.tm_estimate || 0);
  return {
    allowanceTotal: persistedAllowance || _sumTotals(items, isAllowanceLine),
    tmEstimate: persistedTm || _sumTotals(items, isTmLine),
  };
};

/**
 * Public-payload variant. Use this when reading from the safe public
 * payload where T&M lines have already been split out into `data.tm_lines`
 * by the backend projection layer (so the firm `line_items` array no
 * longer contains them). Reads aggregates from `data.totals` directly and
 * falls back to summing whichever arrays are present.
 */
export const deriveDisclosureTotalsFromPublic = (data) => {
  const totals = data?.totals || {};
  const firm = data?.line_items || [];
  const tm = data?.tm_lines || [];
  const persistedAllowance = Number(totals.allowance_total || 0);
  const persistedTm = Number(totals.tm_estimate || 0);
  return {
    allowanceTotal:
      persistedAllowance || _sumTotals(firm, isAllowanceLine),
    tmEstimate: persistedTm || _sumTotals(tm, () => true),
  };
};
